import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNotifications } from '../context/NotificationContext';
import '../styles/NotificationBell.css';

/**
 * Колокольчик уведомлений: бейдж с числом + выпадающий список.
 * Панель рендерится в portal и прижимается к viewport, чтобы не уезжала за край.
 */
const NotificationBell = () => {
  const {
    notifications,
    count,
    panelOpen,
    togglePanel,
    closePanel,
    dismiss,
  } = useNotifications();
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);

  useLayoutEffect(() => {
    if (!panelOpen || !btnRef.current) {
      setPanelStyle(null);
      return undefined;
    }

    const place = () => {
      const btn = btnRef.current.getBoundingClientRect();
      const gutter = 12;
      const maxWidth = Math.min(320, window.innerWidth - gutter * 2);
      // Прижимаем правый край панели к правому краю кнопки, но не выходим за экран
      let right = window.innerWidth - btn.right;
      right = Math.max(gutter, Math.min(right, window.innerWidth - maxWidth - gutter));
      const top = btn.bottom + 8;
      setPanelStyle({
        position: 'fixed',
        top: `${Math.round(top)}px`,
        right: `${Math.round(right)}px`,
        left: 'auto',
        width: `${Math.round(maxWidth)}px`,
      });
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [panelOpen]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onDocClick = (e) => {
      const inBell = rootRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inBell && !inPanel) closePanel();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [panelOpen, closePanel]);

  const panel = panelOpen && panelStyle
    ? createPortal(
      <div
        ref={panelRef}
        className="notification-bell-panel"
        role="dialog"
        aria-label="Уведомления"
        style={panelStyle}
      >
        <div className="notification-bell-panel-title">Уведомления</div>
        {notifications.length === 0 ? (
          <div className="notification-bell-empty">Нет новых уведомлений</div>
        ) : (
          <ul className="notification-bell-list">
            {notifications.map((item) => (
              <li key={item.id} className="notification-bell-item">
                <button
                  type="button"
                  className="notification-bell-item-main"
                  onClick={() => item.onActivate?.()}
                >
                  <span className="notification-bell-item-icon">{item.icon}</span>
                  <span className="notification-bell-item-text">
                    <span className="notification-bell-item-title">{item.title}</span>
                    <span className="notification-bell-item-sub">{item.subtitle}</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="notification-bell-item-dismiss"
                  aria-label="Скрыть"
                  onClick={() => dismiss(item.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>,
      document.body,
    )
    : null;

  return (
    <div className="notification-bell" ref={rootRef}>
      <button
        ref={btnRef}
        type="button"
        className="notification-bell-btn"
        aria-label={count > 0 ? `Уведомления: ${count}` : 'Уведомления'}
        aria-expanded={panelOpen}
        onClick={togglePanel}
      >
        <span className="notification-bell-icon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm8-6V11a8 8 0 1 0-16 0v5l-2 2v1h20v-1l-2-2Z"
              fill="currentColor"
            />
          </svg>
        </span>
        {count > 0 && (
          <span className="notification-bell-badge">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
      {panel}
    </div>
  );
};

export default NotificationBell;
